/**
 * WebView 桥接服务
 * 用于在 WebView 和原生 App 之间进行通信
 */

class WebViewBridge {
  constructor() {
    this.isWebView = false;
    this.platform = null; // 'ios' | 'android' | null
    this.userName = ''; // 存储从 webview 传来的用户名
    this.userId = ''; // 存储用户ID
    this.userInfo = null; // 存储完整的用户信息
    this.init();
  }

  /**
   * 初始化，检测是否在 WebView 环境中
   */
  init() {
    this.isWebView = this.detectWebView();
    this.platform = this.detectPlatform();
    
    // 监听来自原生 App 的消息
    this.setupMessageListener();
    
    // 从 URL 参数获取用户名
    this.getUserNameFromUrl();
    
    // 尝试获取用户信息
    this.tryGetUserInfo();
  }
  
  /**
   * 设置消息监听器，接收来自原生 App 的消息
   */
  setupMessageListener() {
    // 监听来自原生 App 的消息
    window.addEventListener('message', (event) => {
      try {
        // 检查event.data是否存在且不为undefined
        if (!event.data || event.data === 'undefined') {
          return;
        }
        
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data && data.action === 'setUserName' && data.name) {
          this.userName = data.name;
        }
      } catch (error) {
        // 静默处理解析错误，避免在浏览器环境中产生噪音
      }
    });
    
    // iOS WebView 消息监听
    // 预留给 iOS 的消息处理
    
    // Android WebView 消息监听（通过全局函数）
    window.setUserName = (name) => {
      this.userName = name;
    };
  }
  
  /**
   * 从 URL 参数获取用户名（已注释，改为使用原生方法获取）
   * 注意：网站仍会从URL获取character参数作为分享的人物名字，这个逻辑在View.vue中处理
   */
  getUserNameFromUrl() {
    // 注释掉从URL获取用户名的逻辑，改为使用原生webview方法获取用户信息
    // try {
    //   const urlParams = new URLSearchParams(window.location.search);
    //   const name = urlParams.get('name');
    //   if (name) {
    //     this.userName = decodeURIComponent(name);
    //   }
    // } catch (error) {
    //   console.error('从 URL 获取用户名失败:', error);
    // }
  }

  /**
   * 尝试获取用户信息
   */
  tryGetUserInfo() {
    // 只有在webview环境下才尝试获取
    if (this.isWebView && this.platform === 'ios') {
      this.getUserInfo({
        callBack: (obj) => {
          if (obj) {
            this.setUserInfo(obj);
          }
        }
      });
    }
  }

  /**
   * 获取用户信息（按照原生接口规范）
   * @param {object} options - 选项对象，包含callBack回调函数
   */
  getUserInfo(options) {
    if (!options || typeof options.callBack !== 'function') {
      console.error('getUserInfo需要提供callBack回调函数');
      return;
    }

    try {
      // 定义全局回调函数
      window.getUserInfoCallBack = options.callBack;
      
      const requestData = {
        callBack: 'getUserInfoCallBack'
      };
      
      // 检查是否有webkit接口
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.getUserInfo) {
        window.webkit.messageHandlers.getUserInfo.postMessage(JSON.stringify(requestData));
      }
    } catch (error) {
      console.error('调用getUserInfo失败:', error);
    }
  }

  /**
   * 设置用户信息
   * @param {object} userInfo - 用户信息对象
   */
  setUserInfo(userInfo) {
    if (userInfo && typeof userInfo === 'object') {
      this.userInfo = userInfo;
      this.userId = userInfo.userId || userInfo.userld || ''; // 兼容userld拼写
      this.userName = userInfo.userName || '';
    }
  }
  
  /**
   * 获取用户名
   * @returns {string} 用户名
   */
  getUserName() {
    return this.userName;
  }

  /**
   * 获取用户ID
   * @returns {string} 用户ID
   */
  getUserId() {
    return this.userId;
  }

  /**
   * 获取完整用户信息
   * @returns {object|null} 用户信息对象
   */
  getFullUserInfo() {
    return this.userInfo;
  }
  
  /**
   * 设置用户名
   * @param {string} name - 用户名
   */
  setUserName(name) {
    this.userName = name;
  }

  /**
   * 重新获取用户信息
   * @returns {Promise<void>}
   */
  async refreshUserInfo() {
    await this.getUserInfo();
  }

  /**
   * 检测是否在 WebView 中
   */
  detectWebView() {
    const ua = navigator.userAgent.toLowerCase();
    
    // 优先排除微信环境（微信也是 WebView，但我们要用微信 SDK）
    if (this.isWeChat()) {
      return false;
    }
    
    // 最可靠的检测：是否注入了原生对象
    const hasNativeInterface = typeof window.webkit !== 'undefined' || 
                                typeof window.android !== 'undefined' ||
                                typeof window.AppBridge !== 'undefined';
    
    if (hasNativeInterface) {
      return true;
    }
    
    // 检测 iOS WebView（排除 Safari）
    const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(ua);
    
    // 检测 Android WebView
    const isAndroidWebView = ua.includes('wv') || 
                             (ua.includes('android') && !ua.includes('chrome'));
    
    return isIOSWebView || isAndroidWebView;
  }

  /**
   * 检测平台类型
   */
  detectPlatform() {
    if (typeof window.webkit !== 'undefined' || /iphone|ipod|ipad/i.test(navigator.userAgent)) {
      return 'ios';
    }
    if (typeof window.android !== 'undefined' || /android/i.test(navigator.userAgent)) {
      return 'android';
    }
    return null;
  }

  /**
   * 检查是否在微信环境中
   */
  isWeChat() {
    const ua = navigator.userAgent.toLowerCase();
    return /micromessenger/.test(ua);
  }

  /**
   * 向原生 App 发送消息
   * @param {string} action - 操作类型
   * @param {object} data - 数据
   */
  postMessage(action, data = {}) {
    const message = {
      action,
      data,
      timestamp: new Date().toISOString()
    };


    try {
      if (this.platform === 'ios') {
        // iOS WebView 通信
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.AppBridge) {
          window.webkit.messageHandlers.AppBridge.postMessage(message);
          return true;
        }
      } else if (this.platform === 'android') {
        // Android WebView 通信
        if (window.android && typeof window.android.postMessage === 'function') {
          window.android.postMessage(JSON.stringify(message));
          return true;
        }
        // 备用方案：使用 AppBridge
        if (window.AppBridge && typeof window.AppBridge.postMessage === 'function') {
          window.AppBridge.postMessage(JSON.stringify(message));
          return true;
        }
      }

      console.warn('原生桥接对象未找到，消息发送失败');
      return false;
    } catch (error) {
      console.error('发送消息到原生 App 失败:', error);
      return false;
    }
  }

  /**
   * 分享到原生 App
   * @param {object} shareData - 分享数据
   * @param {string} shareData.type - 分享类型: 'webpage' | 'image'
   * @param {string} shareData.title - 分享标题
   * @param {string} shareData.description - 分享描述（可选）
   * @param {string} shareData.url - 分享链接（网页分享）
   * @param {string} shareData.imageUrl - 分享图片URL（图片分享）
   * @param {string} shareData.imageBase64 - 分享图片Base64（图片分享）
   */
  share(shareData) {
    if (!this.isWebView) {
      console.warn('不在 WebView 环境中，无法调用原生分享');
      return false;
    }

    return this.postMessage('share', shareData);
  }

  /**
   * 分享网页链接
   * @param {string} title - 标题
   * @param {string} description - 描述
   * @param {string} url - 链接
   * @param {string} imageUrl - 缩略图URL
   */
  shareWebpage(title, description, url, imageUrl) {
    return this.share({
      type: 'webpage',
      title,
      description,
      url,
      imageUrl
    });
  }

  /**
   * 分享图片
   * @param {string} imageUrl - 图片URL
   * @param {string} imageBase64 - 图片Base64（可选，优先使用）
   * @param {string} title - 标题（可选）
   * @param {string} description - 描述（可选）
   */
  shareImage(imageUrl, imageBase64 = null, title = '', description = '') {
    return this.share({
      type: 'image',
      title,
      description,
      imageUrl,
      imageBase64
    });
  }

  /**
   * 将图片URL转换为Base64
   * @param {string} imageUrl - 图片URL
   * @returns {Promise<string>} Base64字符串
   */
  async imageUrlToBase64(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        try {
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = reject;
      img.src = imageUrl;
    });
  }

  /**
   * 将Canvas转换为Base64
   * @param {HTMLCanvasElement} canvas - Canvas元素
   * @returns {string} Base64字符串
   */
  canvasToBase64(canvas) {
    return canvas.toDataURL('image/png');
  }
}

// 创建单例
const webviewBridge = new WebViewBridge();

export default webviewBridge;
